<template>
<div class="chapter">
<div class="inner_page">
<div class="frontMatterPage" id="fm_2">
<p class="chapterTitle">Acknowledgments</p>
<p class="para">My most pleasant task in writing this history is also the most daunting: to acknowledge the many people whose generous assistance and encouragement have sustained me in my lengthy journey. (I have already acknowledged, of course, my supreme debt to my mentor John Augustine Washington). The long lapse of years makes me fearful that I might accidentally omit some of the scores of correspondents who supplied information on their branch of the Washington family. I have attempted to list them all in the section headed “Contributors” (a wide category that includes postings on the Internet) found near the beginning of my bibliography. At the risk of seeming ungrateful to others, however, I here make an attempt to single out (in alphabetical order) some who have been extraordinarily generous in supplying massive amounts of information.</p>
<p class="para">I must also add that I asked John A. Washington if he had kept a list of correspondents who had assisted him in compiling his monumental “Outline.” He replied that much to his regret he did not, and, for fear of accidentally omitting important helpers, he declined to submit such a list. He did wish, however, that I acknowledge the extraordinary assistance that he received from a genealogist who was a critical partner in his life-long genealogical quest—Prentiss Price. As John would be the first to attest, thousands of names in his Washington “Outline” were collected and contributed by Prentiss Price, who died in August 1979.</p>
<p class="para">I begin with a special “thank you” to my supportive wife, Jody Glenn, who assisted in proofreading and patiently has endured my genealogy addiction. I am also deeply grateful to Ted Savas of Savas Beatie Publishers, who had faith in my unusually lengthy book and took the risk of publishing it in the depths of a severe and seemingly endless economic recession. I am also much indebted to The National Society of the Washington Family Descendants for a subvention toward the publication of this book. Among the many correspondents who contributed family records, the following were especially generous in contributing extensive and critical information:</p>
<p class="NoIndentTop">Stuart Alexander Anderson (Madison, Ala.) </p>
<p class="NoIndent">Robert Stanley Arnold (St. Peters, Mo.) </p>
<p class="NoIndent">Robert James Asbury (Liberty Lake, Wash.) </p>
<p class="NoIndent">Thomas Esrie Ball (Gulf Shores, Ala.) </p>
<p class="NoIndent">James Houston Barr III (Louisville, Ky.).</p>
<p class="NoIndent">Deborah Faye O’Quinn Battles (Paris, Tenn.)</p>
<p class="NoIndent">Marion Singleton Bedinger (Port Angeles, Wash.)</p>
<p class="NoIndent">Kerry Ross Boren (Draper, Ut.) </p>
<p class="NoIndent">Robert Thomas Botts III </p>
<p class="NoIndent">McLemore Bouchelle (Rancho Cucamonga, Cal.) </p>
<p class="NoIndent">James Boulton (Richmond, Va.)</p>
<p class="NoIndent">Jerry C. Breast (Rear Admiral, USN Retired, Nashville, Tenn.)</p>
<p class="NoIndent">Doyle Brittain (Athens, Ga.) </p>
<p class="NoIndent">James Rayford Brotherton (Asheville, N.C.) and Mark Edwin Brotherton (Warner Robins, Ga.) </p>
<p class="NoIndent">Charles Eugene Brown (Ash Grove, Mo.) </p>
<p class="NoIndent">George Landon Browning III (La Cañada Flint Ridge, Cal.) </p>
<p class="NoIndent">Ronald J. Brummette, Jr. (Louisville, Ky.)</p>
<p class="NoIndent">Winstead Thomas Buckner (Lexington, Ky.) </p>
<p class="NoIndent">William Scott Campbell (Valencia, Cal.) </p>
<p class="NoIndent">Mary R. Catalfamo (Manuscript Librarian at Nimitz Library, U.S. Naval Academy, Annapolis)</p>
<p class="NoIndent">Tracy Ayres Cavendish (Hillsboro, Oh.)</p>
<p class="NoIndent">Douglas R. Chandler (Phoenix, Ariz.) </p>
<p class="NoIndent">Patricia Lynn Brown Claytor</p>
<p class="NoIndent">Pat Beeson Coleman (Sulphur Springs, Tex.)</p>
<p class="NoIndent">Margaretta Barton Colt (New York, N.Y.) </p>
<p class="NoIndent">Jane Washington Pendleton Cook (Wilmington, N.C.) </p>
<p class="NoIndent">Davis Griffith-Cox (Terrell, Tex.)</p>
<p class="NoIndent">Charles F. Crabtree (Granbury, Tex.) and Marilyn E. Crabtree Sanderlin (Lake Charles, La.)</p>
<p class="NoIndent">Carla Cramer and Julia Horigan (Tallahassee, Fla.) </p>
<p class="NoIndent">Alice Creighton (chief archivist at the Nimitz Library, U.S. Naval Academy, Annapolis)</p>
<p class="NoIndent">Willis D. Crittenberger (Maj. General, USA Retired)</p>
<p class="NoIndent">Alice Thompson Cross (Virginia Beach, Va.), </p>
<p class="NoIndent">Betsy Bowman Davis</p>
<p class="NoIndent">Estella L. W. Davis (Bonita Springs, Fla.)</p>
<p class="NoIndent">William A. Davis (Gallatin, Tenn.) </p>
<p class="NoIndent">John Thomas DeBell (Catharpin, Va.) </p>
<p class="NoIndent">Michelle Detwiler (Buckley, Wash.) </p>
<p class="NoIndent">Nichole Ashley Dillinger (Raleigh, N.C.) </p>
<p class="NoIndent">John E. Donohew (Rockville, Md.) </p>
<p class="NoIndent">Vernon Henry Drewa (Keller, Tex.) </p>
<p class="NoIndent">Robert Dupree (Tulsa, Okla.), incorporating the research of Emma Rose Moore</p>
<p class="NoIndent">Charlotte Cross Crowder Durham (Coffee County, Tenn.) assisted by George Wayne Chumbley </p>
<p class="NoIndent">Sanford Grant Etheridge (New Orleans, La.) </p>
<p class="NoIndent">James E. Evers (Shreveport, La.) </p>
<p class="NoIndent">Mildred S. Ezell and Margaret P. Ezell, Ph.D. (Germantown, Md.).</p>
<p class="NoIndent">Patrick Anthony Fancher (Marshall, Tex.)</p>
<p class="NoIndent">Randalin Black Ferguson (Princeton, Mo.) assisted by Claire J. Southers (Matthews, N.C.)</p>
<p class="NoIndent">Ruth Williams Finger (Phoenix, Ariz.) </p>
<p class="NoIndent">Wanda Lee Norvell Flynn (Escondido, Cal.)</p>
<p class="NoIndent">William Innis Forbes (Evans Mills, N.Y.) </p>
<p class="NoIndent">Walter E. Forehand (Tallahassee, Fla.)</p>
<p class="NoIndent">Johnabeth Frost (Vinita, Okla.) </p>
<p class="NoIndent">Michael David Frost (Shawnee Mission, Kans.) </p>
<p class="NoIndent">Janet Clayton Gardner (Huntsville, Tex.)</p>
<p class="NoIndent">John H. Garner (Grandview, Mo.)</p>
<p class="NoIndent">Douglas Garnett </p>
<p class="NoIndent">Shirley Stanton Smiley Gartin (Duncanville, Tex.) </p>
<p class="NoIndent">George and Betty Jane (Johnson) Gerber (McLean, Va.)</p>
<p class="NoIndent">Earlene Davison Giglierano (Iowa City, Iowa) </p>
<p class="NoIndent">Eleanor Page Lee Glascock (Upperville, Va.)</p>
<p class="NoIndent">Jerry Glenn (Southgate, Ky.) </p>
<p class="NoIndent">Patricia Ann Broyles Gohlke</p>
<p class="NoIndent">Mary Doris Wright Gooch (Merritt Island, Fla.)</p>
<p class="NoIndent">Donald E. Gradeless (Winona Lake, Ind.) </p>
<p class="NoIndent">Robert Noel Grant (Menlo Park, Cal.) </p>
<p class="NoIndent">John Bachman Lee Greer III (Texarkana, Tex.) assisted by Marinelle Kellner Greer</p>
<p class="NoIndent">Dorothy Delina Groves </p>
<p class="NoIndent">Linda Ann Carstarphen Gugin (Evansville, Ind.) </p>
<p class="NoIndent">Andrew Witold Gutowski (McLean, Va.)</p>
<p class="NoIndent">Gareth Robert Habel (Alexandria, Va.).</p>
<p class="NoIndent">Sue Matych-Hager</p>
<p class="NoIndent">Bobbie Evangeline Owen Haggard (Louisville, Miss.) </p>
<p class="NoIndent">Zadeea Graham Harris (Aztec, N.M.)</p>
<p class="NoIndent">James S. Harry (Baltimore, Md.)</p>
<p class="NoIndent">James Richard Hawks (Olive Branch, Miss.) </p>
<p class="NoIndent">Kathryn Williams Hege (Yadkin County, N.C.) </p>
<p class="NoIndent">Lewis Marshall Helm (Bethesda, Md.) </p>
<p class="NoIndent">Charles Mason Hess</p>
<p class="NoIndent">Truman Hickerson (San Pedro, Cal.)</p>
<p class="NoIndent">Sharon “Kay” Cox Hlava (Greenwood, Ind.) </p>
<p class="NoIndent">Lucille McDaniel Ray Hodges (Abilene, Tex.)</p>
<p class="NoIndent">Armistead Jake Holmes, Jr. (Brewton, Ala.) </p>
<p class="NoIndent">William Alphonso Holtshouser IV (Raleigh, N.C.)</p>
<p class="NoIndent">Rex Hopson (Albuquerque, N.M.) and Merlin Mitchell (Tallahassee, Fla.) </p>
<p class="NoIndent">Dorothy Atkinson Hudson (Brevard, N.C.) </p>
<p class="NoIndent">J. Alan Hunton (Albuquerque, N.M.) </p>
<p class="NoIndent">William Moore Hurst, Jr. (McKinney, Tex.)</p>
<p class="NoIndent">Kristy Louise Hyatt (Augusta, Ga.)</p>
<p class="NoIndent">Eric James (Dana Point, Cal.) </p>
<p class="NoIndent">Linda McGowan Jamison (Liberty, Tex.) </p>
<p class="NoIndent">Katherine Marie Johnson (Honolulu, Hawaii)</p>
<p class="NoIndent">Susan Deupree Jones (Cary, N.C.)</p>
<p class="NoIndent">Karen Jorgensen (Bedford, Tex.)</p>
<p class="NoIndent">Beth Burdick Kalal (Coronado, Cal.)</p>
<p class="NoIndent">Jo Ann Harris Landrum (Conroe, Tex.) </p>
<p class="NoIndent">Claudia Eoline Stewart Lane</p>
<p class="NoIndent">Charles Laurens Latimer, Jr. (Greenville, S.C.) and Edward Brandt Latimer (Columbia, S.C.) </p>
<p class="NoIndent">James Fugate Lawrence (Brig. Gen., USMC Retired, Fort Belvoir, Va.)</p>
<p class="NoIndent">Robert deTreville Lawrence IV (Warrenton, Va.)</p>
<p class="NoIndent">Verdie Denice Jackson Lipscomb (Waxahachie, Tex.) </p>
<p class="NoIndent">Joseph Dandridge Logan III (Roanoke, Va.)</p>
<p class="NoIndent">Peter Hotchkiss Lyons</p>
<p class="NoIndent">Henry C. Mackall (Fairfax, Va.) </p>
<p class="NoIndent">Ross and Virginia Mackenzie (Manakin-Sabot, Va.).</p>
<p class="NoIndent">Lois Maschmeyer (Corning, N.Y.)</p>
<p class="NoIndent">Richard Earl Mather </p>
<p class="NoIndent">Janie Wilkey May</p>
<p class="NoIndent">Janet Gough McMurray</p>
<p class="NoIndent">Patrick Kim McVicker (Toledo, Oh.)</p>
<p class="NoIndent">Paul Mears (Walterboro, S.C.) </p>
<p class="NoIndent">Thomas Glover Medders (West Blocton, Ala.) and Stan Medders (Pacifica, Cal.) </p>
<p class="NoIndent">Anne Manning Miller</p>
<p class="NoIndent">Gerald Alva Miller and his wife Helen Irene Williams Miller (Topeka, Kans.)</p>
<p class="NoIndent">Hal C. Miller (Owensboro, Ky.).</p>
<p class="NoIndent">Lancelot Longstreet Minor III (Memphis, Tenn.)</p>
<p class="NoIndent">Sally Bonham Mohle (Fairfax, Va.) </p>
<p class="NoIndent">Joseph Moore (Henry County, Ga.) </p>
<p class="NoIndent">Mary Utley Murphy (Pewee Valley, Ky.) </p>
<p class="NoIndent">Virginia Sanders Mylius (Birmingham, Ala.) </p>
<p class="NoIndent">Charlene Oerding</p>
<p class="NoIndent">Tammy Howard Ofsanik (Charlotte, N.C.) </p>
<p class="NoIndent">Carolyn and Jerry Pape (Pendleton County, Ky.) </p>
<p class="NoIndent">Thomas W. Pearson (Helena, Mont.) </p>
<p class="NoIndent">Patricia Howard Peterson (Mounds, Okla.) </p>
<p class="NoIndent">Jesse Pettey (Houston, Tex.)</p>
<p class="NoIndent">Anne (Aynn) Puckett Kirtley Phillips (Albany, Ga.)</p>
<p class="NoIndent">Henry T. Poole (Warner Robins, Ga.) </p>
<p class="NoIndent">Elaine Powell (Orlando, Fla.) </p>
<p class="NoIndent">Rita Frances Powers (Manchester, Tenn.) </p>
<p class="NoIndent">Helen Anderson Pruitt (Charleston, S.C.) </p>
<p class="NoIndent">Harry Elwell Raber (Montgomery, Ala.) </p>
<p class="NoIndent">Marianne Rankin (Gulfport, Miss.)</p>
<p class="NoIndent">Robert Scott Reynolds (Scottsboro, Ala.).</p>
<p class="NoIndent">David J. Rice (Hingham, Mass.) </p>
<p class="NoIndent">John Frost Riley (Montevallo, Ala.) </p>
<p class="NoIndent">Rupert Riley (Oak Island, N.C.) </p>
<p class="NoIndent">Adeline Marye Robertson (Arlington, Va.) </p>
<p class="NoIndent">Nola Miles Rogers (Licking County, Oh.) </p>
<p class="NoIndent">Ben Lacy Rose (Richmond, Va.)</p>
<p class="NoIndent">Barbara Elaine Hunt Rowe (Renton, Wash.) </p>
<p class="NoIndent">Katherine Royal (Boxborough, Mass.</p>
<p class="NoIndent">Fred Salter (Palmdale, Cal.) </p>
<p class="NoIndent">Delores Estelle Pickering Sanders (Forest, Miss.) </p>
<p class="NoIndent">Karen and Fleming Saunders V (Burke, Va.) </p>
<p class="NoIndent">Winton Forrest Scott, Jr. (Portland, Me.) </p>
<p class="NoIndent">Sarah Strider Seemann (Rockville, Md.) </p>
<p class="NoIndent">Barbara Linton Segar (King George, Va.) </p>
<p class="NoIndent">Linda Sexton</p>
<p class="NoIndent">David Paul Shaffner </p>
<p class="NoIndent">Mary Washington Shaffner (Alexandria, Va.) </p>
<p class="NoIndent">Sharon Sheets (Loveland, Colo.) </p>
<p class="NoIndent">Eugene Edmund Sherburne (Flippin, Ark.) </p>
<p class="NoIndent">Beth Shields (Seminole, Fla.).</p>
<p class="NoIndent">Carolyn Smotherman (College Grove, Tenn.) </p>
<p class="NoIndent">Brother Thomas W. Spalding, Jr., CFX (Bardstown and Louisville, Ky.) </p>
<p class="NoIndent">Louise Grose Stewart (Lewisville, N.C.) </p>
<p class="NoIndent">William Clarkson Stribling, Jr. (Markham, Va.) </p>
<p class="NoIndent">Mary Kathleen “Corky” Swanson (Abilene, Tex.)</p>
<p class="NoIndent">Roger O. Taylor (Stockton, Cal.) </p>
<p class="NoIndent">William Robert Taylor (Gallatin, Tenn.)</p>
<p class="NoIndent">Carolyn Watson Tharp (Norman, Okla.)</p>
<p class="NoIndent">William Tidwell</p>
<p class="NoIndent">James Irving Tims (Cleveland, Miss.) </p>
<p class="NoIndent">Margaret Kirtley Tippens (Knoxville, Tenn.)</p>
<p class="NoIndent">Ilaine Upton </p>
<p class="NoIndent">Richard Warren Vallandingham (Beaufort, S.C.)</p>
<p class="NoIndent">Lachlan Cumming Vass III (Dallas, Tex.) and Lachlan Maury Vass, Jr. (Bush, La.) </p>
<p class="NoIndent">Robert Brown Veech (Jacksonville, Fla.).</p>
<p class="NoIndent">Lisa Verlo (Los Angeles, Cal.) </p>
<p class="NoIndent">Carcy Koch Vreeland (Denver, Pa.) </p>
<p class="NoIndent">Elreeta Crain Weathers (Hamilton, Tex.)</p>
<p class="NoIndent">Patricia Louise Whipp (La Verne, Cal.)</p>
<p class="NoIndent">Justus Perkins White, Jr. (Sedona, Ariz.) </p>
<p class="NoIndent">Elizabeth Maury (Vass) Guerin Wilkerson (Laurens, South Carolina)</p>
<p class="NoIndent">Helen Jane (Kopecky) Wilson (Rowlett, Tex.) </p>
<p class="NoIndent">James Julius Winn (Reisterstown, Md.) and Julie Sullivan Winn.</p>
<p class="NoIndent">George Edward Withers III </p>
<p class="NoIndent">Norma Harrall Wood (Clinton, Okla.) </p>
<p class="NoIndent">Debra Lee Hardin Woody (Roanoke, Va.) </p>
<p class="NoIndent">Daniel Lloyd Wright (Michigan City, Ind.) </p>
<p class="NoIndent">Norman Thomas Wright, Jr. (Franklin County, Va.) </p>
<p class="NoIndent">Winter Wright (Savannah, Ga.) </p>
<p class="NoIndentTop">Finally, I wish to thank for their patient support a long line of interlibrary loan librarians (and their assistants) at Florida State University’s Strozier Library, especially Phyllis Holzenburg, Carolyn Reynolds, Anna Campbell, Ann Spangler, and Cynthia Hearn.</p>
</div>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// 07_acknowledgement.vue?628b8d14